import { AccessDeniedPage } from '../pages/AccessDeniedPage';
import { NoWebAccessPage } from '../pages/NoWebAccessPage';
import { areaForPrincipal, useRequiredSession, type Area } from '../session';

/**
 * The wall between the back-office and the customer portal.
 *
 * When someone lands in the wrong half of the product they get a page that says
 * so, with a way back to their own area -- not a blank screen, not a silent
 * redirect, and not a spinner. Being told "this is not yours" is information; a
 * screen that simply fails to appear is a support ticket.
 *
 * The refusal is rendered in place rather than redirected to, so the URL stays
 * put. Someone who followed a wrong link can see which link it was, and can
 * report it.
 *
 * As with every guard here, this decides what to *show*. The data is protected
 * by the backend, which scopes every read to the caller's company and answers
 * another company's record with a 404 -- see #206. If this guard were removed
 * tomorrow, a customer user pointed at the back-office would see empty screens
 * and refusals, not somebody else's data.
 */
export function RequireArea({ area, children }: { area: Area; children: React.ReactNode }) {
  const session = useRequiredSession();
  const principalArea = areaForPrincipal(session.principal);

  if (principalArea === null) {
    return <NoWebAccessPage />;
  }

  if (principalArea !== area) {
    return <AccessDeniedPage requestedArea={area} principalArea={principalArea} />;
  }

  return <>{children}</>;
}
